import { useCallback, useEffect, useRef, useState } from "react";
import { QrCode, CheckCircle, XCircle, Camera, CameraSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import QrScanner from "qr-scanner";
import api, { apiError } from "../lib/api";

const extractCode = (text) => {
  const m = String(text || "").match(/DH-[0-9A-Za-z]{4,12}/);
  return (m ? m[0] : String(text || "").trim()).toUpperCase();
};

export default function Scanner() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [redeemed, setRedeemed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [camOn, setCamOn] = useState(false);
  const [camError, setCamError] = useState(null);
  const videoRef = useRef(null);
  const busyRef = useRef(false);

  const lookupCode = useCallback(async (raw) => {
    const c = extractCode(raw);
    if (!c) return;
    setLoading(true);
    setResult(null);
    setRedeemed(false);
    setCode(c);
    try {
      const r = await api.get(`/coupons/lookup/${encodeURIComponent(c)}`);
      setResult(r.data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
      busyRef.current = false;
    }
  }, []);

  const lookup = (e) => {
    e?.preventDefault();
    if (!code.trim()) return;
    lookupCode(code);
  };

  // Fotocamera: attiva lo scan continuo; al primo QR letto chiude e cerca il coupon
  useEffect(() => {
    if (!camOn || !videoRef.current) return undefined;
    let scanner = null;
    try {
      scanner = new QrScanner(videoRef.current, (res) => {
        if (busyRef.current) return;
        busyRef.current = true;
        setCamOn(false);
        lookupCode(res?.data || "");
      }, {
        preferredCamera: "environment",
        highlightScanRegion: true,
        highlightCodeOutline: true,
        returnDetailedScanResult: true,
      });
      scanner.start().catch(() => {
        setCamError("Fotocamera non disponibile o permesso negato. Usa il codice manuale.");
        setCamOn(false);
      });
    } catch {
      setCamError("Fotocamera non supportata da questo browser. Usa il codice manuale.");
      setCamOn(false);
      return undefined;
    }
    return () => { try { scanner.stop(); scanner.destroy(); } catch { /* noop */ } };
  }, [camOn, lookupCode]);

  // Apertura diretta con ?code= (QR stampato sul coupon del cliente)
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("code");
    if (q) lookupCode(q);
  }, [lookupCode]);

  const redeem = async () => {
    try {
      const r = await api.post("/coupons/redeem", { code: result.code });
      toast.success(r.data.reservation_linked ? "Coupon validato e collegato alla prenotazione di oggi" : "Coupon validato");
      setRedeemed(true);
      setResult({ ...result, status: "utilizzato", is_valid: false });
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-4 max-w-lg" data-testid="scanner-page">
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Scanner QR</h1>
      <p className="text-sm text-zinc-500">Inquadra il QR del coupon con la fotocamera oppure inserisci il codice manualmente.</p>

      <div className="bg-white border border-zinc-200 rounded-lg p-4 space-y-3" data-testid="scanner-camera-card">
        {!camOn ? (
          <button onClick={() => { setCamError(null); setCamOn(true); }} data-testid="scanner-camera-on"
            className="w-full h-14 rounded-md text-white font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition"
            style={{ backgroundColor: "#01B2E0" }}>
            <Camera size={20} weight="bold" /> Attiva fotocamera
          </button>
        ) : (
          <div className="space-y-2">
            <div className="relative rounded-lg overflow-hidden bg-zinc-900">
              <video ref={videoRef} autoPlay playsInline muted data-testid="scanner-video"
                className="w-full aspect-[4/3] object-cover" />
            </div>
            <p className="text-[11px] text-zinc-500 text-center">Inquadra il QR sul coupon del cliente: la ricerca parte in automatico.</p>
            <button onClick={() => setCamOn(false)} data-testid="scanner-camera-off"
              className="w-full h-10 rounded-md bg-white border border-zinc-300 text-xs font-semibold text-zinc-700 flex items-center justify-center gap-2">
              <CameraSlash size={16} /> Chiudi fotocamera
            </button>
          </div>
        )}
        {camError && (
          <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="scanner-camera-error">
            {camError}
          </div>
        )}
      </div>

      <form onSubmit={lookup} className="flex gap-2">
        <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Codice coupon (es. DH-BENV20)"
          data-testid="scanner-code-input"
          className="flex-1 h-12 px-3 rounded-md border border-zinc-300 text-sm font-mono bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900" />
        <button type="submit" disabled={loading} data-testid="scanner-lookup-btn"
          className="h-12 px-5 rounded-md bg-zinc-900 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
          <QrCode size={18} weight="bold" /> Cerca
        </button>
      </form>

      {result && (
        <div className={`bg-white border-2 rounded-lg p-5 space-y-3 ${
          result.status === "utilizzato" || redeemed ? "border-zinc-300" : result.is_valid ? "border-emerald-400" : "border-rose-300"}`}
          data-testid="scanner-result">
          <div className="flex items-center gap-2">
            {result.status === "utilizzato" || redeemed ? (
              <><CheckCircle size={22} className="text-zinc-500" /><span className="font-bold text-zinc-700">Già utilizzato</span></>
            ) : result.is_valid ? (
              <><CheckCircle size={22} className="text-emerald-600" weight="fill" /><span className="font-bold text-emerald-700">Coupon valido</span></>
            ) : (
              <><XCircle size={22} className="text-rose-600" weight="fill" /><span className="font-bold text-rose-700">Non valido / scaduto</span></>
            )}
          </div>
          <div className="font-display font-bold text-xl text-zinc-900" data-testid="scanner-coupon-name">{result.coupon_name}</div>
          <p className="text-sm text-zinc-600">{result.description}</p>
          <div className="text-sm text-zinc-700 grid gap-1">
            <div><span className="text-zinc-500">Cliente:</span> <strong data-testid="scanner-customer">{result.customer_name}</strong> · {result.customer_phone}</div>
            <div><span className="text-zinc-500">Validità:</span> {result.valid_from} → {result.valid_to}</div>
            {result.conditions && <div><span className="text-zinc-500">Condizioni:</span> {result.conditions}</div>}
            {(result.referred_count || 0) > 0 && (
              <div className="mt-1 flex items-center gap-1.5 bg-amber-50 border border-amber-300 text-amber-800 rounded-md px-3 py-2 text-sm font-semibold"
                data-testid="scanner-referral-bonus">
                <span>🎁 Bonus passaparola attivo: {result.referred_count} amic{result.referred_count === 1 ? "o ha" : "i hanno"} prenotato dal suo link — <strong>applica 30€</strong></span>
              </div>
            )}
            <div><span className="text-zinc-500">Codice:</span> <span className="font-mono">{result.code}</span></div>
          </div>
          {result.is_valid && !redeemed && (
            <button onClick={redeem} data-testid="scanner-redeem-btn"
              className="w-full h-12 rounded-md bg-emerald-600 text-white font-bold text-sm hover:bg-emerald-700 active:scale-[0.98] transition">
              VALIDA COUPON
            </button>
          )}
          {redeemed && (
            <div className="text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2" data-testid="scanner-redeemed">
              Utilizzo registrato. Il coupon è ora collegato alla prenotazione del cliente, se presente oggi.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
